/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable max-len */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { CatalogSource } from '@1po/1po-bff-fe-spec/generated/common/vehicle/CatalogSource';
import { VehicleDetail } from '@1po/1po-bff-fe-spec/generated/common/vehicle/VehicleDetail';
import { RefSelectProps } from 'antd/lib/select';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';
import { ROUTER_CATALOG_DH, ROUTER_CATALOG_IAM, ROUTER_CATALOG_VEHICLE } from 'app/AppRouter';
import { AngleRightIcon, RedoIcon, SearchIcon } from 'assets/icons';
import { DataContainer, ErrorWithLabel } from 'components/DataContainer';
import { fetchVehicleByBMM } from 'domains/catalog/Catalog.requests';
import {
  fetchSearchBMMVehicleBrandsSaga,
  getIAMVehicleBrands,
  getLastSearchedVehicleKey,
  getLastVehicleDetail,
  getVehicleBrands,
  searchIAMBMMVehicleSaga,
} from 'domains/catalog/Catalog.store';
import { IAMVehicleBrandLocal, VehicleBrandLocal } from 'domains/catalog/Catalog.types';
import {
  BlackButton,
  Box,
  CenteredSpin,
  ErrorAlert,
  Flex,
  FloatingCard,
  FloatingContent,
  Icon,
  Input,
  MarginBox,
  Pipeline,
  Select,
  SelectOption,
  Spin,
  WhiteButton,
  WithTooltip,
} from 'UI';
import { getData, isLoading, NO_DATA, rollDownEffect } from 'utils';
import { useFocus } from 'utils/hooks/useFocus';
import { SearchVariantOption, SearchVariantType, SelectSearchVariant } from './SearchBar';
import { SearchByModelDH } from './SearchByModelDH';
import { SearchByModelIAM } from './SearchByModelIAM';

const SEARCH_BY_MODEL_CARD_HEIGHT = 400;

export type SearchByModelStateValue = {
  brand?: string;
  modelFamily?: string;
  modelType?: string;
  engine?: string;
  gearbox?: string;
  country?: string;
  model?: string;
  version?: string;
};

export type SearchByModelState = SearchByModelStateValue & {
  stateId: string;
};

export type SearchByModelOptions = {
  brands?: Array<VehicleBrandLocal | IAMVehicleBrandLocal> | NO_DATA;
};

export interface FormControl {
  formState: SearchByModelState;
  setFormState: (x: SearchByModelState) => void;
  // formStateIndex: number;
  formOptions: SearchByModelOptions;
}

const SStepRowContainer = styled.div<{ $disabled?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 12px;
  min-height: 40px;
  background: ${({ theme }) => theme.color.white};
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  width: 100%;

  &::after {
    content: '';
    position: absolute;
    left: 56px;
    right: 28px;
    bottom: 0;
    border-bottom: 1px solid ${({ theme }) => theme.color.grey90};
  }

  &:hover {
    background: ${({ $disabled, theme }) => ($disabled ? theme.color.grey95 : theme.color.grey90)};
  }
`;

export const SStepsContainer = styled.div`
  & > *:last-child ${SStepRowContainer}::after {
    border-bottom: none;
  }
`;

const SStepBadge = styled.span<{ $disabled?: boolean }>`
  width: 24px;
  height: 24px;
  min-width: 24px;
  border-radius: 6px;
  background: ${({ $disabled, theme }) => ($disabled ? theme.color.grey65 : theme.color.brand_black)};
  color: ${({ theme }) => theme.color.white};
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const SRowSelectBase = styled(Select)`
  flex: 1;
  .ant-select-selector {
    border: none !important;
    box-shadow: none !important;
    background: transparent !important;
    padding: 0 !important;
    height: 32px !important;
    font-size: 13px;
  }
  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    font-size: 13px;
    line-height: 32px !important;
    padding-left: 0 !important;
  }
  .ant-select-selection-search {
    left: 0 !important;
    right: 0 !important;
  }
  .ant-select-selection-search-input {
    padding-left: 0 !important;
  }
  .ant-select-selection-placeholder {
    color: ${({ theme }) => theme.color.grey15};
    opacity: 1;
  }
  &.ant-select-disabled .ant-select-selection-placeholder {
    color: ${({ theme }) => theme.color.grey65};
  }
  .ant-select-selection-item {
    color: ${({ theme }) => theme.color.status_info_active};
    font-weight: 600;
  }
  .ant-select-arrow {
    display: none;
  }
  &.ant-select-disabled .ant-select-selector {
    background: transparent !important;
  }
  .ant-select-item {
    font-size: 13px;
    min-height: 28px;
    padding: 4px 12px;
    line-height: 20px;
  }
  .ant-select-item-option-active:not(.ant-select-item-option-disabled) {
    background: ${({ theme }) => theme.color.grey95};
  }
  .ant-select-item-option-content {
    font-size: 13px;
  }
`;
const SRowSelect = SRowSelectBase as typeof Select;

const SActionRow = styled.div`
  display: flex;
  gap: 12px;
  padding-top: 16px;
`;

const hasMultiChoiceOptions = (options: SelectOption[]) => {
  if (!Array.isArray(options) || options.length === 0) {
    return false;
  }
  if (options.length > 1) {
    return true;
  }
  const firstOption = options[0];
  return 'options' in firstOption && firstOption.options.length > 1;
};

export const SearchByModelAcceptButton = ({
  catalogSource,
  formControl,
  pendingQuery,
  setPendingQuery,
  requestId,
  isFormCompleted,
  onReset,
}: {
  catalogSource: CatalogSource;
  formControl: FormControl;
  pendingQuery: string | undefined;
  setPendingQuery: (x: string | undefined) => void;
  requestId: string;
  isFormCompleted: boolean;
  onReset?: () => void;
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const formState = formControl.formState;

  const performSearch = () => {
    if (formState.brand && formState.engine && formState.modelType && formState.modelFamily && formState.gearbox) {
      setPendingQuery(requestId);
      fetchVehicleByBMM(
        dispatch,
        formState.brand,
        formState.modelFamily,
        formState.modelType,
        formState.engine,
        formState.gearbox,
        '',
        true,
        catalogSource,
      );
      return;
    }
    if (formState.brand && formState.model && formState.version) {
      setPendingQuery(requestId);
      dispatch(
        searchIAMBMMVehicleSaga({
          brandCode: formState.brand,
          modelCode: formState.model,
          versionCode: formState.version,
          requestId,
        }),
      );
    }
  };

  useHotkeys(
    'enter',
    (event) => {
      event.preventDefault();
      if (isFormCompleted) {
        performSearch();
      }
    },
    [isFormCompleted],
  );

  return (
    <SActionRow>
      <WhiteButton onClick={onReset} stretch shape={'round'}>
        <Flex align={'center'} justify={'center'} gap={8}>
          <Icon IconComponent={RedoIcon} size={14} color={'brand_black'} />
          {t('catalog.parts.search.by_model.reset_fields', 'Reset fields')}
        </Flex>
      </WhiteButton>
      {pendingQuery === requestId ? (
        <Flex align={'center'} justify={'center'} size={1}>
          <Spin />
        </Flex>
      ) : (
        <BlackButton onClick={performSearch} disabled={!isFormCompleted} stretch shape={'round'}>
          <Flex align={'center'} justify={'center'} gap={8}>
            <Icon IconComponent={SearchIcon} size={14} color={'white'} />
            {t('catalog.parts.search.by_model.identify_vehicle', 'Identify vehicle')}
          </Flex>
        </BlackButton>
      )}
    </SActionRow>
  );
};

// eslint-disable-next-line complexity
export function SelectStep({
  formMapping,
  formIndex,
  formStateIndex,
  placeholder,
  formControl,
  options,
  autoFocusStep,
}: Readonly<{
  formMapping: Array<keyof SearchByModelStateValue>;
  formIndex: number;
  formStateIndex: number;
  placeholder: string;
  formControl: FormControl;
  options: SelectOption[];
  autoFocusStep?: boolean;
}>) {
  const ref = useRef<RefSelectProps>(null);
  const propName = formMapping[formIndex];
  const { formState, setFormState } = formControl;
  const value = formState[propName];
  const isActive = formIndex === formStateIndex;
  const shouldAutoFocusStep = autoFocusStep ?? true;
  const hasMultipleOptions = hasMultiChoiceOptions(options);

  // open dropdown if the is more than one option, or more than one option in option groups
  useFocus<RefSelectProps>(ref, [shouldAutoFocusStep && isActive && hasMultipleOptions]);

  // this useEffect fills input automatically if there is only one item in options
  useEffect(() => {
    if (
      isActive &&
      options &&
      Array.isArray(options) &&
      options.length === 1 &&
      ('value' in options[0] || ('options' in options[0] && options[0].options.length === 1))
    ) {
      setFormState({
        ...formState,
        [propName]: 'value' in options[0] ? options[0].value : options[0].options[0].value,
      });
    }
  }, [Array.isArray(options) ? options.reduce((acc, next) => acc + '-' + next, '') : options, isActive, ref]);

  const handleClear = () =>
    formMapping.reduce((acc, next, index) => (formIndex <= index ? { ...acc, [next]: undefined } : acc), formState);

  const isDisabled = formIndex > formStateIndex;
  return (
    <SStepRowContainer $disabled={isDisabled}>
      <SStepBadge $disabled={isDisabled}>{formIndex + 1}</SStepBadge>
      <SRowSelect
        notFoundContent={<ErrorAlert message={'No matches found'} />}
        onChange={(value) => {
          if (value) {
            setFormState({ ...handleClear(), [propName]: String(value) });
          }
          ref.current !== null && ref.current.blur();
        }}
        value={value}
        options={Array.isArray(options) ? options : []}
        placeholder={placeholder}
        disabled={isDisabled}
        search
        alignLeft
        size={'middle'}
        allowClear
        onClear={() => {
          setFormState(handleClear());
        }}
        passRef={ref}
        autoOpen={shouldAutoFocusStep}
      />
      <Icon IconComponent={AngleRightIcon} size={12} color={isDisabled ? 'grey65' : 'grey45'} />
    </SStepRowContainer>
  );
}

export function SearchByModel({
  searchVariant,
  setSearchVariant,
  searchVariantOptions,
  setIsChangeVehiclePopupVisible,
  onFound,
  hideVariantSelector,
  plain,
}: Readonly<{
  searchVariant: SearchVariantType;
  setSearchVariant: (x: SearchVariantType) => void;
  searchVariantOptions: SearchVariantOption[];
  setIsChangeVehiclePopupVisible?: (a: boolean) => void;
  onFound?: (vehicleDetail: VehicleDetail) => void;
  hideVariantSelector?: boolean;
  plain?: boolean;
}>) {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const [formState, setFormStateBase] = useState<SearchByModelState>({ stateId: '' });
  const [brandOptions, setBrandOptions] = useState<Array<VehicleBrandLocal | IAMVehicleBrandLocal> | NO_DATA>(
    undefined,
  );
  const [pendingQuery, setPendingQuery] = useState<string | undefined>(undefined);
  const [formStateTitle, setFormStateTitle] = useState('');

  const setFormState = (x: SearchByModelState) => setFormStateBase({ ...x, stateId: uuid() });
  const formControl = { formState, setFormState, formOptions: { brands: brandOptions } };

  const brandsDH = useSelector(getVehicleBrands);
  const brandsIAM = useSelector(getIAMVehicleBrands);

  const brandsDataDH = getData(brandsDH) ?? [];
  const brandsDataIAM = getData(brandsIAM) ?? [];
  const brands = useMemo(
    () => [...brandsDataDH, ...brandsDataIAM] as Array<VehicleBrandLocal | IAMVehicleBrandLocal> | undefined,
    [brandsDataDH, brandsDataIAM],
  );
  const brandsDep = brands?.reduce((acc, next) => acc + '-' + next, '');
  const lastSearchedVehicleKey = useSelector(getLastSearchedVehicleKey);
  const lastSearchedVehicleDetail = useSelector(getLastVehicleDetail);

  useEffect(() => {
    if (!brandsDH && !isLoading(brandsDH)) {
      dispatch(fetchSearchBMMVehicleBrandsSaga());
    }
  }, [brandsDH]);

  useEffect(() => {
    setBrandOptions(brands);
  }, [brandsDep]);

  useEffect(() => {
    if (pendingQuery && lastSearchedVehicleKey === pendingQuery) {
      setIsChangeVehiclePopupVisible && setIsChangeVehiclePopupVisible(false);
      setTimeout(() => {
        //300ms for animation of closing popup
        setSearchVariant('VINVRN');
      }, 300);

      if (onFound && lastSearchedVehicleDetail !== undefined) {
        onFound(lastSearchedVehicleDetail);
        return;
      }

      // Hack - IAM has only 3 fields
      if (lastSearchedVehicleKey.split('_').length === 3) {
        history.push(`${ROUTER_CATALOG_IAM}/${lastSearchedVehicleKey}${ROUTER_CATALOG_VEHICLE}`);
      } else {
        history.push(`${ROUTER_CATALOG_DH}/${lastSearchedVehicleKey}${ROUTER_CATALOG_VEHICLE}`);
      }
    }
  }, [lastSearchedVehicleKey, pendingQuery]);

  const selectedBrand = Array.isArray(brands) && brands?.find((brand) => brand.code === formState.brand);
  const isSelectedBrandRenaultOrDacia = !selectedBrand || (selectedBrand && selectedBrand.source === 'DH');

  useEffect(() => {
    if (plain) {
      return;
    }
    rollDownEffect(
      'search-by-model-card',
      'search-by-model-content',
      !isSelectedBrandRenaultOrDacia,
      SEARCH_BY_MODEL_CARD_HEIGHT,
    );
  }, [isSelectedBrandRenaultOrDacia, plain]);

  const content = (
    <>
      {!hideVariantSelector && (
        <>
          <Flex>
            <Box width={1} />
            <SelectSearchVariant
              searchVariant={searchVariant}
              setSearchVariant={setSearchVariant}
              searchVariantOptions={searchVariantOptions}
            />
            <Pipeline size={50} offsetRatio={8} />
            <Flex size={9}>
              <WithTooltip title={formStateTitle}>
                <div style={{ width: '100%' }}>
                  <Input
                    onChange={() => undefined}
                    value={formStateTitle}
                    placeholder={t('catalog.parts.search.by_model.hint', 'Information filled progressively...')}
                    readOnly
                    size={'large'}
                    ellipsis
                  />
                </div>
              </WithTooltip>
            </Flex>
          </Flex>
          <Pipeline size={'100%'} horizontal />
        </>
      )}
      <Box height={14} />
      <Flex justify={'center'} align={'flex-start'}>
        {isSelectedBrandRenaultOrDacia ? (
          <DataContainer
            data={brandsDH}
            Loading={() => (
              <MarginBox mt={60}>
                <CenteredSpin />
              </MarginBox>
            )}
            ErrorState={() => (
              <MarginBox mt={45}>
                <ErrorWithLabel />
              </MarginBox>
            )}
          >
            <SearchByModelDH
              brands={brands}
              formControl={formControl}
              pendingQuery={pendingQuery}
              setPendingQuery={setPendingQuery}
              formStateTitle={formStateTitle}
              setFormStateTitle={setFormStateTitle}
              autoFocusStep={!plain}
            />
          </DataContainer>
        ) : (
          <DataContainer
            data={brandsIAM}
            ErrorState={() => (
              <MarginBox mt={45}>
                <ErrorWithLabel />
              </MarginBox>
            )}
          >
            <SearchByModelIAM
              brands={brands}
              formControl={formControl}
              pendingQuery={pendingQuery}
              setPendingQuery={setPendingQuery}
              formStateTitle={formStateTitle}
              setFormStateTitle={setFormStateTitle}
              autoFocusStep={!plain}
              plain={plain}
            />
          </DataContainer>
        )}
      </Flex>
    </>
  );

  if (plain) {
    return content;
  }

  return (
    <FloatingCard id={'search-by-model-card'} large={!isSelectedBrandRenaultOrDacia}>
      <FloatingContent id={'search-by-model-content'}>{content}</FloatingContent>
    </FloatingCard>
  );
}
